import React, { useMemo } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  FullWidthFieldContainer,
  FullWidthTextField,
  SingleItemRowContainer,
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import { OrderActionsProps, ShippingStatus } from "./Order.types";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import OrderStatus from "./OrderStatus";

const getStyles = (theme: Theme) => ({
  OrderActionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
});

export const OrderCancellationRequestedActions: React.FC<OrderActionsProps> = (
  props
): React.JSX.Element => {
  const { orderItemAction, onOrderItemActionUpdate, styles } = props;

  logDebug("OrderCancellationRequestedActions PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useMemo(() => getStyles(theme), [theme]);
  const effectiveStyles = useMemo(
    () =>
      styles === undefined
        ? defaultStyles
        : mergeProperties(defaultStyles, styles),
    [defaultStyles, styles]
  );

  logDebug("OrderCancellationRequestedActions effectiveStyles");
  logDebug(effectiveStyles);

  const handleTrackingNumberValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.trackingNumber = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleNoteValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.note = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleIssueRefundValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.issueRefund = !newOrderItemAction.issueRefund;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    selection: ShippingStatus
  ) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.shippingStatus = selection;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const children = [
    <ToggleButton
      value={ShippingStatus.CANCEL_REQUESTED}
      key={ShippingStatus.CANCEL_REQUESTED}
    >
      <OrderStatus
        status={ShippingStatus.CANCEL_REQUESTED}
        isAdmin={true}
        displayChipForZeroCount
        disabled={
          orderItemAction.shippingStatus !== ShippingStatus.CANCEL_REQUESTED
        }
        styles={effectiveStyles}
      />
    </ToggleButton>,
    <ToggleButton
      value={ShippingStatus.CANCELLED}
      key={ShippingStatus.CANCELLED}
    >
      <OrderStatus
        status={ShippingStatus.CANCELLED}
        isAdmin={true}
        label="Process Cancel"
        displayChipForZeroCount
        disabled={orderItemAction.shippingStatus !== ShippingStatus.CANCELLED}
        styles={effectiveStyles}
      />
    </ToggleButton>,
  ];

  const control = {
    value: orderItemAction.shippingStatus,
    onChange: handleChange,
    exclusive: true,
  };

  return (
    <>
      <Grid sx={effectiveStyles.OrderActionContainer}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ToggleButtonGroup size="small" {...control} aria-label="Small sizes">
            {children}
          </ToggleButtonGroup>
        </Stack>
        {orderItemAction.shippingStatus !== ShippingStatus.CANCEL_REQUESTED && (
          <>
            <SingleItemRowContainer
              styles={{ PackableSingleItemRowContainer: { pt: 2 } }}
            >
              <FullWidthFieldContainer>
                <FullWidthTextField
                  id="note"
                  label="Note"
                  value={orderItemAction.note}
                  onChange={handleNoteValueChange}
                />
              </FullWidthFieldContainer>
            </SingleItemRowContainer>
            <SingleItemRowContainer>
              <FullWidthFieldContainer>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={orderItemAction.issueRefund}
                      onChange={handleIssueRefundValueChange}
                    />
                  }
                  label="Issue Item Refund"
                />
              </FullWidthFieldContainer>
            </SingleItemRowContainer>
          </>
        )}
      </Grid>
    </>
  );
};

export default OrderCancellationRequestedActions;
